"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import {
  type FullBrandTheme,
  themeToStyleVars,
  hexToHsl,
} from "@/lib/utils/derive-palette";
import temaService, { type PersonalTema } from "@/lib/services/tema-service";
import { cacheBrandLogo } from "@/components/dashboard/brand-logo";
import {
  CHAVE_TEMA_DA_MARCA,
  limparMarcaDoAparelho,
} from "@/lib/tema/marca-no-aparelho";
import { corDaBarraNaTela } from "@/lib/tema/cor-da-barra";

interface BrandThemeContextValue {
  brandTheme: FullBrandTheme | null;
  isLoading: boolean;
  previewTheme: (theme: FullBrandTheme | null) => void;
  saveTheme: (
    tema: Partial<Omit<PersonalTema, "id" | "personal_id">>,
  ) => Promise<void>;
  refetchTheme: () => Promise<void>;
  secoesVisiveis: Record<string, boolean> | null;
  logoUrl: string | null;
  logoUrlLight: string | null;
  logoTitulo: string | null;
  logoEscala: number;
  setLogoUrl: (url: string | null) => void;
  setLogoUrlLight: (url: string | null) => void;
  setLogoTitulo: (titulo: string | null) => void;
  setLogoEscala: (escala: number) => void;
}

const BrandThemeContext = createContext<BrandThemeContextValue>({
  brandTheme: null,
  isLoading: false,
  previewTheme: () => {},
  saveTheme: async () => {},
  refetchTheme: async () => {},
  secoesVisiveis: null,
  logoUrl: null,
  logoUrlLight: null,
  logoTitulo: null,
  logoEscala: 1,
  setLogoUrl: () => {},
  setLogoUrlLight: () => {},
  setLogoTitulo: () => {},
  setLogoEscala: () => {},
});

/* A chave vem da lista de `marca-no-aparelho`, que é quem sabe apagar tudo. */
const CACHE_KEY = CHAVE_TEMA_DA_MARCA;

/** Todas as CSS vars que o brand theme injeta inline, usadas para limpar no revert. */
const BRAND_CSS_VARS = [
  "--primary",
  "--primary-foreground",
  "--primary-dim",
  "--primary-glow",
  "--primary-glow-strong",
  "--nav-active-bg",
  "--nav-active-fg",
  "--ring",
  "--sidebar-primary",
  "--sidebar-primary-foreground",
  "--shadow-glow",
  "--shadow-glow-lg",
  "--bg",
  "--bg-elevated",
  "--surface-1",
  "--surface-2",
  "--surface-3",
  "--surface-4",
  "--surface-hover",
  "--card",
  "--popover",
  "--input",
  "--secondary",
  "--muted",
  "--accent",
] as const;

/** Envia cores pro shell nativo (StatusBar, background do WebView) */
function sendThemeToNative(theme: FullBrandTheme | null) {
  try {
    if (!(window as any).ReactNativeWebView) return;
    /* Sem fundo próprio da marca (é o caso de toda marca hoje: nenhuma tela grava
       mais `cor_background`), vale a cor da barra do tema na tela. Era o escuro
       fixo, e no tema claro a casca punha a hora e a bateria em branco por cima
       da página clara. */
    const bgColor = theme?.bg || corDaBarraNaTela();
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        type: "THEME_UPDATE",
        background_color: bgColor,
        status_bar_color: bgColor,
      }),
    );
  } catch {}
}

function applyThemeToDocument(theme: FullBrandTheme | null) {
  const html = document.documentElement;

  if (!theme) {
    html.removeAttribute("data-brand");
    for (const prop of BRAND_CSS_VARS) {
      html.style.removeProperty(prop);
    }
    return;
  }

  html.setAttribute("data-brand", "true");
  const vars = themeToStyleVars(theme);
  for (const [key, value] of Object.entries(vars)) {
    html.style.setProperty(key, value);
  }
}

function cacheTheme(theme: FullBrandTheme | null) {
  try {
    if (theme) {
      localStorage.setItem(CACHE_KEY, JSON.stringify(theme));
    } else {
      localStorage.removeItem(CACHE_KEY);
    }
  } catch {}
}

function loadCachedTheme(): FullBrandTheme | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) return JSON.parse(cached);
  } catch {}
  return null;
}


function apiTemaToFullTheme(tema: PersonalTema): FullBrandTheme {
  // Só a cor primária e as superfícies vêm da API, os tons derivados
  // (dim, glow, foreground, item ativo) saem dela em `themeToStyleVars`.
  return {
    primary: tema.cor_primaria,
    primaryDim: tema.cor_primaria_dim || undefined,
    bg: tema.cor_background || undefined,
    surface1: tema.cor_surface_1 || undefined,
    surface2: tema.cor_surface_2 || undefined,
    surface3: tema.cor_surface_3 || undefined,
    surface4: tema.cor_surface_4 || undefined,
  };
}

export function BrandThemeProvider({ children }: { children: ReactNode }) {
  const [brandTheme, setBrandTheme] = useState<FullBrandTheme | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [secoesVisiveis, setSecoesVisiveis] = useState<Record<
    string,
    boolean
  > | null>(null);
  const [logoUrl, setLogoUrlState] = useState<string | null>(null);
  const [logoUrlLight, setLogoUrlLightState] = useState<string | null>(null);
  const [logoTitulo, setLogoTitulo] = useState<string | null>(null);
  const [logoEscala, setLogoEscalaState] = useState<number>(1);

  const setLogoUrl = useCallback((url: string | null) => {
    setLogoUrlState(url);
    cacheBrandLogo(url);
  }, []);

  const setLogoUrlLight = useCallback((url: string | null) => {
    setLogoUrlLightState(url);
    try {
      if (url) localStorage.setItem("wt_brand_logo_light", url);
      else localStorage.removeItem("wt_brand_logo_light");
    } catch {}
  }, []);

  const setLogoEscala = useCallback((escala: number) => {
    setLogoEscalaState(escala);
    try {
      localStorage.setItem("wt_brand_logo_escala", String(escala));
    } catch {}
  }, []);

  const setLogoTituloAndCache = useCallback((titulo: string | null) => {
    setLogoTitulo(titulo);
    try {
      if (titulo) {
        localStorage.setItem("wt_brand_logo_titulo", titulo);
      } else {
        localStorage.removeItem("wt_brand_logo_titulo");
      }
    } catch {}
  }, []);

  // Aplicar tema cacheado imediatamente (evita flash)
  useEffect(() => {
    const cached = loadCachedTheme();
    if (cached) {
      setBrandTheme(cached);
      applyThemeToDocument(cached);
    }
    // Carregar logo do cache
    try {
      const cachedLogo = localStorage.getItem("wt_brand_logo");
      if (cachedLogo) setLogoUrlState(cachedLogo);
      const cachedLogoLight = localStorage.getItem("wt_brand_logo_light");
      if (cachedLogoLight) setLogoUrlLightState(cachedLogoLight);
      const cachedTitulo = localStorage.getItem("wt_brand_logo_titulo");
      if (cachedTitulo) setLogoTitulo(cachedTitulo);
      const cachedEscala = localStorage.getItem("wt_brand_logo_escala");
      if (cachedEscala) setLogoEscalaState(parseFloat(cachedEscala) || 1);
    } catch {}
  }, []);

  // Controle de concorrência: apenas a última chamada aplica o resultado
  const fetchIdRef = useRef(0);

  // Buscar tema da API
  const fetchTheme = useCallback(async () => {
    const thisId = ++fetchIdRef.current;
    setIsLoading(true);
    try {
      const userStr = localStorage.getItem("welltrainer_user");
      if (!userStr) return;

      const user = JSON.parse(userStr);
      let tema: PersonalTema | null = null;

      if (user.type === "PERSONAL") {
        tema = await temaService.buscarMeuTema();
      } else if (user.type === "TRAINEE" || user.type === "ALUNO") {
        tema = await temaService.buscarTemaDoMeuPersonal();
      }

      // Se outra chamada foi iniciada enquanto esta rodava, ignorar resultado
      if (fetchIdRef.current !== thisId) return;

      if (tema) {
        /* Quem diz se a personalização vale é o SERVIDOR (`personalizacao_ativa`),
           porque ele é o único que conhece o plano de agora e o direito adquirido.
           `false` é ordem de limpeza: apaga cor, logo, nome e escala do aparelho.

           A dedução pela cor continua embaixo, só para a API que ainda não manda o
           campo. Ela nunca deu conta do caso que importa: dizia "não personalizou"
           e "personalizou e perdeu" da mesma forma, então a marca antiga só saía
           da tela quando cada campo chegava nulo, e o cache do celular a
           reaplicava antes de toda resposta. */
        const perdeuODireito = tema.personalizacao_ativa === false;

        const primaryNormalized = (tema.cor_primaria || "")
          .toLowerCase()
          .trim();
        const isDefaultPrimary =
          !primaryNormalized || primaryNormalized === "#d7f205";
        const hasCustomSurfaces = !!(
          tema.cor_background ||
          tema.cor_surface_1 ||
          tema.cor_surface_2 ||
          tema.cor_surface_3 ||
          tema.cor_surface_4
        );
        const shouldClearTheme =
          perdeuODireito || (isDefaultPrimary && !hasCustomSurfaces);

        if (!shouldClearTheme) {
          const fullTheme = apiTemaToFullTheme(tema);
          setBrandTheme(fullTheme);
          applyThemeToDocument(fullTheme);
          sendThemeToNative(fullTheme);
          cacheTheme(fullTheme);
        } else {
          // Tema padrão, remover qualquer override customizado que tenha sobrado
          // de um plano anterior (ex: downgrade Grátis → Solo).
          setBrandTheme(null);
          applyThemeToDocument(null);
          sendThemeToNative(null);
          cacheTheme(null);
        }

        if (perdeuODireito) {
          /* Estado E aparelho: sem apagar as chaves, o próximo carregamento
             aplicaria o logo e o nome guardados antes de a API responder, e a
             marca voltaria a piscar na tela do aluno em cada abertura. */
          setLogoUrl(null);
          setLogoUrlLight(null);
          setLogoTituloAndCache(null);
          setLogoEscala(1);
          limparMarcaDoAparelho();
        } else {
          setLogoUrl(tema.logo_url || null);
          setLogoUrlLight(tema.logo_url_light || null);
          setLogoTituloAndCache(tema.logo_titulo || null);
          setLogoEscala(tema.logo_escala ?? 1);
        }
        // Extrair seções visíveis
        if (tema.secoes_visiveis) {
          try {
            const parsed =
              typeof tema.secoes_visiveis === "string"
                ? JSON.parse(tema.secoes_visiveis)
                : tema.secoes_visiveis;
            setSecoesVisiveis(parsed);
          } catch {}
        }
      } else {
        // Sem tema customizado, limpar cache e usar padrão
        setBrandTheme(null);
        applyThemeToDocument(null);
        sendThemeToNative(null);
        cacheTheme(null);
        setLogoUrl(null);
        setLogoUrlLight(null);
        setLogoTituloAndCache(null);
        setLogoEscala(1);
        /* A conta não tem tema nenhum: o que estiver guardado é de outra conta
           (o aparelho é o mesmo) ou de um tema que foi apagado. */
        limparMarcaDoAparelho();
      }
    } catch (err) {
      // Se outra chamada já iniciou, ignorar erro desta
      if (fetchIdRef.current !== thisId) return;
      console.warn("[BrandTheme] Falha ao buscar tema da API:", err);
      // Falha na API, manter cache se existir
    } finally {
      if (fetchIdRef.current === thisId) {
        setIsLoading(false);
      }
    }
  }, [setLogoUrl, setLogoTituloAndCache]);

  useEffect(() => {
    fetchTheme();
  }, [fetchTheme]);

  // Re-buscar tema quando o login acontecer
  useEffect(() => {
    const handler = () => {
      fetchTheme();
    };
    window.addEventListener("welltrainer:auth-change", handler);
    return () => window.removeEventListener("welltrainer:auth-change", handler);
  }, [fetchTheme]);

  const previewTheme = useCallback((theme: FullBrandTheme | null) => {
    setBrandTheme(theme);
    applyThemeToDocument(theme);
  }, []);

  const saveTheme = useCallback(
    async (tema: Partial<Omit<PersonalTema, "id" | "personal_id">>) => {
      const saved = await temaService.atualizarTema(tema);
      const fullTheme = apiTemaToFullTheme(saved);
      setBrandTheme(fullTheme);
      applyThemeToDocument(fullTheme);
      cacheTheme(fullTheme);
      /* O que voltou do servidor manda, inclusive quando volta vazio. Sem isto, o
       cache do aparelho guardava o logo antigo depois de "Tirar" e ele
       reaparecia no próximo carregamento. */
      if (tema.logo_url !== undefined) setLogoUrl(saved.logo_url || null);
      if (tema.logo_url_light !== undefined)
        setLogoUrlLight(saved.logo_url_light || null);
      if (tema.logo_escala !== undefined) setLogoEscala(saved.logo_escala ?? 1);
    },
    [setLogoUrl, setLogoUrlLight, setLogoEscala],
  );

  return (
    <BrandThemeContext.Provider
      value={{
        brandTheme,
        isLoading,
        previewTheme,
        saveTheme,
        refetchTheme: fetchTheme,
        secoesVisiveis,
        logoUrl,
        logoUrlLight,
        logoTitulo,
        logoEscala,
        setLogoUrl,
        setLogoUrlLight,
        setLogoTitulo: setLogoTituloAndCache,
        setLogoEscala,
      }}
    >
      {children}
    </BrandThemeContext.Provider>
  );
}

export function useBrandTheme() {
  return useContext(BrandThemeContext);
}
